import { useCallback, useState } from "react";

import { Layout, LayoutItem } from "react-grid-layout";
import { useDebouncedCallback } from "use-debounce";

import {
  GRID_COLUMNS,
  GRID_GAP,
  ROW_HEIGHT,
} from "src/cases/templates/caseBlocks/gridConstants";
import { blocksToLayout } from "src/cases/templates/caseBlocks/rglTypes";
import { CaseBlockConfigFormValue } from "src/cases/templates/formUtils/types";

export type AddBlockPreviewLayoutItem = LayoutItem & { static: true };

type UseGridHoverOptions = {
  blocks: CaseBlockConfigFormValue[];
  containerWidth: number;
};

type UseGridHoverReturn = {
  clearAddBlockPreviewItem: () => void;
  addBlockPreviewItem: AddBlockPreviewLayoutItem | null;
  handleMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: () => void;
};

/**
 * Preview block sizes to try when hovering over empty grid spaces.
 * Ordered from largest to smallest (minimum 2x1).
 */
export const ADD_BLOCK_PREVIEW_SIZES: Array<{ w: number; h: number }> = [
  { w: 3, h: 2 }, // largest
  { w: 3, h: 1 },
  { w: 2, h: 2 },
  { w: 2, h: 1 }, // minimum
];

export const ADD_BLOCK_PREVIEW_ID = "__preview__";

/**
 * Maximum number of rows to scan when finding a valid position.
 * This is a reasonable upper bound for grid layouts.
 */
const MAX_GRID_ROWS = 100;

export const calculateCellWidth = (containerWidth: number): number => {
  return (containerWidth - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
};

export const pixelToColumn = (x: number, containerWidth: number): number => {
  const cellWidth = calculateCellWidth(containerWidth);
  const column = Math.floor(x / (cellWidth + GRID_GAP));
  return Math.max(0, Math.min(GRID_COLUMNS - 1, column));
};

export const rowToPixelRange = (
  row: number,
  h: number,
): { minY: number; maxY: number } => {
  const minY = row * (ROW_HEIGHT + GRID_GAP);
  const maxY = minY + h * ROW_HEIGHT + (h - 1) * GRID_GAP;
  return { minY, maxY };
};

export const canFitAddBlockPreview = (
  layout: Layout,
  x: number,
  y: number,
  w: number,
  h: number,
): boolean => {
  // Check bounds
  if (x < 0 || x + w > GRID_COLUMNS || y < 0) {
    return false;
  }

  // Check no collision with any block
  for (const item of layout) {
    const overlapsX = x < item.x + item.w && x + w > item.x;
    const overlapsY = y < item.y + item.h && y + h > item.y;
    if (overlapsX && overlapsY) {
      return false;
    }
  }
  return true;
};

export const findCompactedY = (
  layout: Layout,
  x: number,
  w: number,
  h: number,
): number | null => {
  for (let y = 0; y <= MAX_GRID_ROWS; y++) {
    if (canFitAddBlockPreview(layout, x, y, w, h)) {
      return y;
    }
  }
  return null;
};

export const findValidAddBlockPreviewItem = (
  layout: Layout,
  cursorCol: number,
  cursorPixelY: number,
): AddBlockPreviewLayoutItem | null => {
  // Try each size from largest to smallest
  for (const size of ADD_BLOCK_PREVIEW_SIZES) {
    // Calculate centered X (cursor in middle of button)
    const centeredX = Math.max(
      0,
      Math.min(GRID_COLUMNS - size.w, cursorCol - Math.floor(size.w / 2)),
    );

    // Try positions around the centered position
    const xPositions = [centeredX, centeredX + 1, centeredX - 1].filter(
      (x) => x >= 0 && x + size.w <= GRID_COLUMNS,
    );

    for (const x of xPositions) {
      // Calculate compacted Y position
      const compactedY = findCompactedY(layout, x, size.w, size.h);

      // Skip if no valid position exists (grid is full at this x position)
      if (compactedY === null) {
        continue;
      }

      // Check if cursor X is within preview bounds
      if (cursorCol < x || cursorCol >= x + size.w) {
        continue;
      }

      // Check if cursor Y is within the compacted preview's pixel bounds
      const { minY, maxY } = rowToPixelRange(compactedY, size.h);
      if (cursorPixelY >= minY && cursorPixelY < maxY) {
        return {
          i: ADD_BLOCK_PREVIEW_ID,
          x,
          y: compactedY,
          w: size.w,
          h: size.h,
          static: true,
        };
      }
    }
  }

  return null;
};

/**
 * Hook to track mouse position relative to grid container and calculate
 * valid block preview position using RGL's compaction logic.
 *
 * Returns a LayoutItem that can be added directly to GridLayout's layout prop.
 */
export const useGridHover = ({
  blocks,
  containerWidth,
}: UseGridHoverOptions): UseGridHoverReturn => {
  const [addBlockPreviewItem, setAddBlockPreviewItem] =
    useState<AddBlockPreviewLayoutItem | null>(null);

  const calculateAddBlockPreviewItem = useCallback(
    (clientX: number, clientY: number, rect: DOMRect) => {
      if (containerWidth === 0) {
        setAddBlockPreviewItem(null);
        return;
      }

      const relativeX = clientX - rect.left;
      const relativeY = clientY - rect.top;

      if (
        relativeX < 0 ||
        relativeY < 0 ||
        relativeX > rect.width ||
        relativeY > rect.height
      ) {
        setAddBlockPreviewItem(null);
        return;
      }

      const layout = blocksToLayout(blocks);
      const targetColumn = pixelToColumn(relativeX, containerWidth);
      const item = findValidAddBlockPreviewItem(
        layout,
        targetColumn,
        relativeY,
      );

      setAddBlockPreviewItem(item);
    },
    [blocks, containerWidth],
  );

  const debouncedCalculate = useDebouncedCallback(
    calculateAddBlockPreviewItem,
    16,
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      debouncedCalculate(event.clientX, event.clientY, rect);
    },
    [debouncedCalculate],
  );

  const handleMouseLeave = useCallback(() => {
    debouncedCalculate.cancel();
    setAddBlockPreviewItem(null);
  }, [debouncedCalculate]);

  const clearAddBlockPreviewItem = handleMouseLeave;

  return {
    clearAddBlockPreviewItem,
    addBlockPreviewItem,
    handleMouseMove,
    handleMouseLeave,
  };
};
